<template>
  <div class="grid grid-flow-row gap-4">
    <div class="flex justify-center">
      <h1 class="text-3xl font-bold text-gray-900">Mi Perfil de Empresa</h1>
    </div>

    <!-- estados -->
    <div v-if="loading" class="text-center text-gray-600">Cargando perfil de la empresa...</div>
    <div v-else-if="error" class="text-center text-red-500">{{ error }}</div>

    <!-- contenido principal -->
    <div v-else class="p-6 bg-white rounded-lg shadow-md">
      <div class="flex items-center justify-between">
        <h2 class="text-2xl font-semibold text-gray-800">Datos de la Empresa</h2>

        <div class="flex gap-2">
          <button
            v-if="!isEditing"
            @click="startEdit"
            class="px-4 py-2 text-white rounded-lg bg-[#3edfa9] hover:bg-[#ffe068]"
          >
            Editar
          </button>
          <template v-else>
            <button
              @click="save"
              class="px-4 py-2 text-white  rounded-lg bg-[#3edfa9] hover:bg-[#ffe068]"
            >
              Guardar
            </button>
            <button
              @click="cancelEdit"
              class="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
            >
              Cancelar
            </button>
          </template>
        </div>
      </div>

      <!-- VISTA SOLO LECTURA -->
      <div v-if="!isEditing" class="mt-6 grid md:grid-cols-2 gap-4 text-sm">
        <template v-for="field in fieldsReadOnly" :key="field.key">
          <!-- Celda especial: bannerUrl como preview con altura de input -->
          <div v-if="field.key === 'bannerUrl'">
            <div class="text-gray-500">{{ field.label }}</div>
            <div
              class="mt-1 w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 min-h-[42px] text-gray-800
                     flex items-center gap-3"
            >
              <img
                v-if="resolvedBannerUrl && !imageError"
                :src="resolvedBannerUrl"
                :alt="perfil.nombreEmpresa || perfil.name || 'Logo'"
                class="h-8 w-auto object-contain shrink-0"
                @error="imageError = true"
              />
              <div
                v-else
                class="h-8 w-8 rounded bg-gray-200 text-gray-600 flex items-center justify-center text-xs font-semibold shrink-0"
                title="Sin imagen"
              >
                {{ initials }}
              </div>
              <span class="truncate text-gray-600">
                {{ resolvedBannerUrl || 'Sin imagen' }}
              </span>
            </div>
          </div>

          <!-- Celdas normales -->
          <div v-else>
            <div class="text-gray-500">{{ field.label }}</div>
            <div class="mt-1 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 min-h-[42px] text-gray-800">
              <span v-if="perfil[field.key] !== undefined && perfil[field.key] !== null && String(perfil[field.key]).length">
                {{ perfil[field.key] }}
              </span>
              <span v-else class="text-gray-400 italic">Sin datos</span>
            </div>
          </div>
        </template>
      </div>

      <!-- FORMULARIO EDICIÓN -->
      <form v-else class="mt-6 grid md:grid-cols-2 gap-4" @submit.prevent>
        <template v-for="field in fieldsEdit" :key="field.key">
          <!-- Celda especial: bannerUrl como preview clickeable, misma altura que un input -->
          <div v-if="field.key === 'bannerUrl'">
            <label :for="field.key" class="block text-sm font-medium text-gray-700">
              {{ field.label }}
            </label>

            <!-- Caja con estilo de input y altura 42px aprox -->
            <div
              class="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 min-h-[42px] shadow-sm
                     focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500
                     flex items-center gap-3 cursor-pointer"
              @click="changeBannerUrl"
              title="Click para pegar URL del logo"
            >
              <img
                v-if="resolvedBannerUrlEdit && !imageErrorEdit"
                :src="resolvedBannerUrlEdit"
                alt="Preview Logo"
                class="h-8 w-auto object-contain shrink-0"
                @error="imageErrorEdit = true"
              />
              <div
                v-else
                class="h-8 w-8 rounded bg-gray-100 text-gray-500 flex items-center justify-center text-xs font-semibold shrink-0"
              >
                {{ initialsForm }}
              </div>
              <span class="truncate text-gray-700">
                {{ normalizedBannerForDisplay || 'Click para pegar URL...' }}
              </span>
            </div>
          </div>

          <!-- Celdas normales -->
          <div v-else>
            <label :for="field.key" class="block text-sm font-medium text-gray-700">
              {{ field.label }}
            </label>
            <input
              :id="field.key"
              :type="field.type || 'text'"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              v-model="form[field.key]"
              :required="field.required || false"
              :placeholder="placeholders[field.key] || ''"
            />
          </div>
        </template>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import authService from '../../composables/api/authService'
import tenantService from '../../composables/api/tenantService'
import { API_URL } from '../../config.js'

/** Campos (orden en el grid). Incluir bannerUrl dentro del grid para ocupar su misma celda. */
const fieldsReadOnly = [
  { key: 'nombreEmpresa',   label: 'Nombre de la Empresa' },
  { key: 'descripcion',     label: 'Descripción' },
  { key: 'bannerUrl',       label: 'Logo (URL del Banner)' }, // ← celda especial
  { key: 'legajo',          label: 'Legajo' },
  { key: 'direccionFiscal', label: 'Dirección Fiscal' },
  { key: 'cuit',            label: 'CUIT' },
  { key: 'telefono',        label: 'Teléfono' },
  { key: 'email',           label: 'Email' },
  { key: 'numeroIIBB',      label: 'Número IIBB' },
  { key: 'condicionIVA',    label: 'Condición IVA' },
]

const fieldsEdit = [
  { key: 'name',            label: 'Usuario (interno)', required: true },
  { key: 'nombreEmpresa',   label: 'Nombre de la Empresa' },
  { key: 'descripcion',     label: 'Descripción' },
  { key: 'bannerUrl',       label: 'Logo (URL del Banner)' }, // ← celda especial clickeable
  { key: 'legajo',          label: 'Legajo' },
  { key: 'direccionFiscal', label: 'Dirección Fiscal' },
  { key: 'cuit',            label: 'CUIT' },
  { key: 'telefono',        label: 'Teléfono' },
  { key: 'email',           label: 'Email', type: 'email' },
  { key: 'numeroIIBB',      label: 'Número IIBB' },
  { key: 'condicionIVA',    label: 'Condición IVA' },
]

/** Placeholders opcionales */
const placeholders = {
  name: 'mi-empresa (requerido)',
}

/** Mapeos API */
function fromApiTenant(api) {
  if (!api) return {}
  return {
    id: api.id ?? null,
    name: api.name ?? '',
    nombreEmpresa: api.nombreEmpresa ?? '',
    descripcion: api.descripcion ?? '',
    bannerUrl: api.bannerUrl ?? '',
    legajo: api.legajo ?? '',
    direccionFiscal: api.direccionFiscal ?? '',
    telefono: api.telefono ?? '',
    email: api.email ?? '',
    numeroIIBB: api.numeroIIBB ?? '',
    cuit: api.cuit ?? '',
    condicionIVA: api.condicionIVA ?? '',
    isActive: api.isActive ?? true,
  }
}
function toApiTenant(ui) {
  const clean = (v) => (typeof v === 'string' ? v.trim() : v)
  const orNull = (v) => {
    const c = clean(v)
    return c === '' || c === undefined ? null : c
  }
  return {
    name: clean(ui.name), // requerido por API
    nombreEmpresa: orNull(ui.nombreEmpresa),
    descripcion: orNull(ui.descripcion),
    bannerUrl: orNull(ui.bannerUrl),
    isActive: ui.isActive ?? true,
    legajo: orNull(ui.legajo),
    direccionFiscal: orNull(ui.direccionFiscal),
    telefono: orNull(ui.telefono),
    email: orNull(ui.email),
    numeroIIBB: orNull(ui.numeroIIBB),
    cuit: orNull(ui.cuit),
    condicionIVA: orNull(ui.condicionIVA),
  }
}

/** Normalizador de URL para mostrar/preview */
const formatImageUrl = (raw) => {
  if (!raw) return ''
  const s = String(raw).trim()
  if (/^(data:|blob:|https?:\/\/)/i.test(s)) return s
  if (/^[\w.-]+\.[a-z]{2,}(\/.*)?$/i.test(s)) return `https://${s}`
  const base = (API_URL || '').replace(/\/$/, '')
  const path = s.replace(/^\//, '')
  return `${base}/${path}`
}

/** Estado */
const perfil = ref({})
const allFormKeys = [
  'name','nombreEmpresa','descripcion','bannerUrl','legajo','direccionFiscal','cuit','telefono','email','numeroIIBB','condicionIVA'
]
const form = reactive(Object.fromEntries(allFormKeys.map(k => [k, ''])))

const loading = ref(true)
const error = ref(null)
const isEditing = ref(false)

const tenantId = ref(null)
const tenantRecordId = ref(null)

/** Preview / fallbacks */
const imageError = ref(false)
const imageErrorEdit = ref(false)

const initials = computed(() => {
  const src = (perfil.value?.nombreEmpresa || perfil.value?.name || '').trim()
  if (!src) return '—'
  const parts = src.split(/\s+/).slice(0, 2)
  return parts.map(p => p[0]?.toUpperCase() || '').join('')
})
const initialsForm = computed(() => {
  const src = (form?.nombreEmpresa || form?.name || '').trim()
  if (!src) return '—'
  const parts = src.split(/\s+/).slice(0, 2)
  return parts.map(p => p[0]?.toUpperCase() || '').join('')
})

/** URLs resueltas */
const resolvedBannerUrl = computed(() => formatImageUrl(perfil.value?.bannerUrl || ''))
const resolvedBannerUrlEdit = computed(() => formatImageUrl(form.bannerUrl || ''))
const normalizedBannerForDisplay = computed(() => resolvedBannerUrlEdit.value || '')

/** Carga Perfil */
async function fetchPerfil () {
  loading.value = true
  error.value = null
  imageError.value = false
  imageErrorEdit.value = false
  try {
    const loggedUser = await authService.getCurrentUser()
    tenantId.value = loggedUser?.tenantId || null

    if (tenantId.value) {
      const tenantData = await tenantService.getTenantByTenantId(tenantId.value)
      perfil.value = fromApiTenant(tenantData)
      tenantRecordId.value = perfil.value.id ?? tenantData?.tenantId ?? null
    } else {
      perfil.value = {}
      tenantRecordId.value = null
    }
  } catch (e) {
    console.error(e)
    error.value = 'Error al cargar el perfil: ' + (e?.message || String(e))
  } finally {
    loading.value = false
  }
}

/** Edición */
function startEdit () {
  for (const k of allFormKeys) form[k] = perfil.value?.[k] ?? ''
  imageErrorEdit.value = false
  isEditing.value = true
}
function cancelEdit () {
  isEditing.value = false
}

/** Acciones logo */
function changeBannerUrl () {
  const nueva = window.prompt('Pegá la URL del logo (http/https, dominio, o ruta relativa):', form.bannerUrl || '')
  if (nueva === null) return
  form.bannerUrl = (nueva || '').trim()
  imageErrorEdit.value = false
}

/** Guardar */
async function save () {
  if (!form.name?.trim()) {
    alert('El campo "Usuario (interno)" es requerido')
    return
  }
  try {
    loading.value = true
    const payload = toApiTenant(form)

    if (tenantRecordId.value) {
      await tenantService.updateTenant(tenantRecordId.value, { id: tenantRecordId.value, ...payload })
    } else {
      const created = await tenantService.createTenant(payload)
      tenantRecordId.value = created?.id ?? created?.tenantId ?? null
    }

    isEditing.value = false
    await fetchPerfil()
  } catch (e) {
    const serverMsg = e?.response?.data?.message
      || e?.response?.data?.Message
      || JSON.stringify(e?.response?.data)
      || e?.message
      || String(e)
    console.error('Guardar perfil - detalle backend:', e?.response || e)
    error.value = 'Error al guardar: ' + serverMsg
  } finally {
    loading.value = false
  }
}

onMounted(fetchPerfil)
</script>

<style scoped>
.grid-flow-row { display: grid; grid-auto-flow: row; gap: 1rem; }
</style>
